import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Users, Store, Package, DollarSign, ShieldCheck, ShieldX, Power, Star, Eye, EyeOff, Flag, Phone, Trash2, KeyRound, MessageCircle, Search, FileText } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatGNF, STATUS_LABEL, STATUS_COLOR, shortTag } from "@/utils/format";
import AdminFinance from "@/components/AdminFinance";
import ApplicationsPanel from "@/components/admin/ApplicationsPanel";
import { DriversPanel } from "@/components/admin/DriversPanel";
import { EmailLogsPanel } from "@/components/admin/EmailLogsPanel";
import AdminIncidentsPanel from "@/components/admin/AdminIncidentsPanel";
import DeleteAccountModal from "@/components/admin/DeleteAccountModal";
import DeletedAccountsPanel from "@/components/admin/DeletedAccountsPanel";
import ChangeRequestsPanel from "@/components/admin/ChangeRequestsPanel";
import { PreflightPanel } from "@/components/admin/PreflightPanel";
import AdminSecurityPanel from "@/components/admin/AdminSecurityPanel";
import RejectReplyModal from "@/components/admin/RejectReplyModal";
import CancelOrderModal from "@/components/CancelOrderModal";
import CancelInDeliveryModal from "@/components/admin/CancelInDeliveryModal";
import RestaurantDocuments from "@/components/admin/RestaurantDocuments";
import ContactMessagesPanel from "@/components/admin/ContactMessagesPanel";
import ClientsPanel from "@/components/admin/ClientsPanel";
import DeliveryZonesPanel from "@/components/admin/DeliveryZonesPanel";
import PhotoLightbox from "@/components/PhotoLightbox";
import { imgUrl } from "@/utils/format";
import { toast } from "sonner";

export default function AdminConsole() {
  const { t } = useTranslation();
  const tr = t; // alias — « t » est masqué par la variable de boucle des onglets
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  // Deep link from a notification (?tab=incidents / ?tab=applications)
  useEffect(() => {
    const u = searchParams.get("tab");
    if (u) setTab(u);
  }, [searchParams]);
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [appsSummary, setAppsSummary] = useState({ pending_drivers: 0, pending_restaurants: 0 });
  const [openIncidents, setOpenIncidents] = useState(0);
  const [pendingChanges, setPendingChanges] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind, id, name, role }
  const [rejectTarget, setRejectTarget] = useState(null); // review dont on refuse la réponse
  const [lightbox, setLightbox] = useState(null); // { photos, index }
  const [selectedRestos, setSelectedRestos] = useState([]); // rids sélectionnés pour liaison enseigne
  const [linkModal, setLinkModal] = useState(false); // modal choix succursale par défaut
  const [primaryId, setPrimaryId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // commande à annuler (admin)
  const [cancelInDelivery, setCancelInDelivery] = useState(null); // annulation en cours de livraison
  const [docsTarget, setDocsTarget] = useState(null); // fiche documents restaurant {restaurant_id, data}

  const openDocs = async (rid) => {
    try {
      const { data } = await apiClient.get(`/admin/restaurants/${rid}/documents`);
      setDocsTarget(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const [resetResult, setResetResult] = useState(null); // { name, phone, temp_password }
  const [userSearch, setUserSearch] = useState("");
  const [restoSearch, setRestoSearch] = useState("");

  // Onglet Restaurants : tri alphabétique + recherche rapide (nom / zone / statut)
  const filteredRestaurants = React.useMemo(() => {
    const s = restoSearch.trim().toLowerCase();
    return [...restaurants]
      .filter((r) => {
        if (!s) return true;
        return (r.name || "").toLowerCase().includes(s)
          || (r.zone || "").toLowerCase().includes(s)
          || (r.status || "").toLowerCase().includes(s);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
  }, [restaurants, restoSearch]);

  // Onglet Utilisateurs : tri alphabétique + recherche rapide (nom / téléphone / email)
  const filteredUsers = React.useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const qDigits = q.replace(/[^\d]/g, "");
    return [...users]
      .filter((u) => {
        if (!q) return true;
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const phone = (u.phone || "").replace(/[^\d]/g, "");
        return name.includes(q) || email.includes(q) || (qDigits && phone.includes(qDigits));
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
  }, [users, userSearch]);

  const resetPassword = async (u) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.name} ? Un mot de passe temporaire sera généré.`)) return;
    try {
      const { data } = await apiClient.post(`/admin/users/${u.user_id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.resetFail"));
    }
  };

  const fetchAll = () => {
    apiClient.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    apiClient.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    apiClient.get("/admin/restaurants").then(({ data }) => setRestaurants(data)).catch(() => {});
    apiClient.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    apiClient.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => {});
    apiClient.get("/admin/candidatures/summary").then(({ data }) => setAppsSummary(data)).catch(() => {});
    apiClient.get("/admin/incidents/count-open").then(({ data }) => setOpenIncidents(data.open || 0)).catch(() => {});
    apiClient.get("/admin/change-requests?status=pending").then(({ data }) => setPendingChanges(data.pending_count || 0)).catch(() => {});
  };

  useEffect(() => {
    if (user?.role === "admin") fetchAll();
  }, [user]);

  if (!user || user.role !== "admin") {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-mute">Accès réservé aux administrateurs.</div>;
  }

  const toggleStatus = async (rid, status) => {
    await apiClient.patch(`/admin/restaurants/${rid}?status=${status}`);
    toast.success(t("admin.restaurantsTab.updated"));
    fetchAll();
  };
  const toggleOpen = async (rid, open) => {
    await apiClient.patch(`/admin/restaurants/${rid}?open=${open}`);
    fetchAll();
  };

  const moderate = async (reviewId, action, reason = null) => {
    await apiClient.patch(`/admin/reviews/${reviewId}`, { action, reason });
    const msgs = {
      approve: t("admin.approveDone"),
      hide: t("admin.reviewsTab.hideDone"),
      show: "Avis remis en ligne",
      flag: t("admin.reviewsTab.flagDone"),
      unflag: t("admin.reviewsTab.unflagDone"),
      approve_reply: t("admin.approveReplyDone"),
      reject_reply: t("admin.rejectReplyDone"),
    };
    toast.success(msgs[action] || t("admin.reviewUpdated"));
    fetchAll();
  };

  const rejectReply = async (reason) => {
    await moderate(rejectTarget.review_id, "reject_reply", reason);
    setRejectTarget(null);
  };

  const pendingReviewsCount = reviews.filter((r) => r.status === "pending" || (r.reply && r.reply_status === "pending")).length;

  const toggleSelectResto = (rid) =>
    setSelectedRestos((prev) => (prev.includes(rid) ? prev.filter((x) => x !== rid) : [...prev, rid]));

  const linkChain = async () => {
    try {
      const { data } = await apiClient.post("/admin/chains/link", { restaurant_ids: selectedRestos, primary_id: primaryId });
      toast.success(`Enseigne créée : ${data.count} succursales liées`);
      setLinkModal(false); setSelectedRestos([]); setPrimaryId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la liaison");
    }
  };

  const unlinkChain = async (rid) => {
    await apiClient.post("/admin/chains/unlink", { restaurant_id: rid });
    toast.success("Succursale déliée de l'enseigne");
    fetchAll();
  };

  const importRfc = async () => {
    try {
      const { data } = await apiClient.post("/admin/seed/rfc");
      toast.success(`Import RFC : ${data.restaurants} restaurants, ${data.dishes} plats, ${data.users} comptes créés (${data.skipped_restaurants} déjà présents)`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import RFC");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-brand-red">{tr("admin.consoleKicker")}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-6">{tr("admin.dashboardTitle")}</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={<Package />} label={tr("admin.scOrders")} value={stats?.total_orders ?? 0} color="#E32636" />
        <StatCard icon={<DollarSign />} label={tr("admin.scRevenue")} value={formatGNF(stats?.revenue ?? 0)} color="#009E60" />
        <StatCard icon={<Store />} label={tr("admin.scActiveRestos")} value={`${stats?.active_restaurants ?? 0} / ${stats?.total_restaurants ?? 0}`} color="#F4C430" />
        <StatCard icon={<Users />} label={tr("admin.scClients")} value={stats?.total_users ?? 0} color="#1A73E8" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-elevated rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-mute">{tr("admin.scCommission")}</div>
            <div className="font-display text-2xl font-semibold">{formatGNF(stats?.commission ?? 0)}</div>
          </div>
          <ShieldCheck className="w-8 h-8 text-brand-green" />
        </div>
        <div className="bg-elevated rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-mute">{tr("admin.scZones")}</div>
            <div className="font-display text-2xl font-semibold">{tr("admin.scZonesValue")}</div>
          </div>
          <Store className="w-8 h-8 text-brand-red" />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6">
        {["overview", "finance", "applications", "change-requests", "incidents", "restaurants", "drivers", "clients", "users", "deleted", "orders", "zones", "reviews", "messages", "emails", "preflight", "securite"].map((t) => {
          const pendingApps = appsSummary.pending_drivers + appsSummary.pending_restaurants;
          return (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`rounded-full px-4 py-2 text-sm font-medium border whitespace-nowrap ${tab === t ? "bg-ink text-white border-ink" : "bg-white border-sand"}`}>
              {{ overview: tr("admin.tabs.overview"), finance: tr("admin.tabs.finance"), applications: `${tr("admin.ctabApplications")}${pendingApps ? ` (${pendingApps})` : ""}`, "change-requests": `${tr("admin.ctabChangeRequests")}${pendingChanges ? ` (${pendingChanges})` : ""}`, incidents: `${tr("admin.ctabIncidents")}${openIncidents ? ` (${openIncidents})` : ""}`, restaurants: tr("admin.tabs.restaurants"), drivers: tr("admin.ctabDrivers"), clients: tr("admin.ctabClients"), users: tr("admin.tabs.users"), deleted: tr("admin.ctabDeleted"), orders: tr("admin.tabs.orders"), reviews: `${tr("admin.tabs.reviews")}${pendingReviewsCount ? ` (${pendingReviewsCount})` : ""}`, messages: tr("admin.ctabMessages"), emails: tr("admin.ctabEmails"), preflight: "Preflight", securite: "Sécurité" }[t]}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="bg-white border border-sand rounded-2xl p-6">
          <h3 className="font-display text-xl font-semibold mb-4">{tr("admin.overview.latestOrders")}</h3>
          <div className="space-y-2 text-sm">
            {orders.slice(0, 10).map((o) => (
              <div key={o.order_id} className="flex justify-between border-b border-sand pb-2">
                <span>#{o.order_id.slice(-6).toUpperCase()} · {o.restaurant_name}</span>
                <span className="text-mute">{formatGNF(o.total)}</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[o.status]+"22", color: STATUS_COLOR[o.status] }}>{tr(`status.${o.status}`, STATUS_LABEL[o.status])}</span>
              </div>
            ))}
            {orders.length === 0 && <div className="text-mute">Aucune commande encore.</div>}
          </div>
        </div>
      )}

      {tab === "finance" && <AdminFinance />}

      {tab === "applications" && <ApplicationsPanel />}

      {tab === "drivers" && <DriversPanel />}

      {tab === "emails" && <EmailLogsPanel />}

      {tab === "preflight" && <PreflightPanel />}

      {tab === "securite" && <AdminSecurityPanel />}

      {tab === "change-requests" && <ChangeRequestsPanel onChanged={fetchAll} />}

      {tab === "incidents" && <AdminIncidentsPanel />}

      {tab === "deleted" && <DeletedAccountsPanel />}

      {tab === "messages" && <ContactMessagesPanel />}

      {tab === "clients" && <ClientsPanel />}

      {tab === "zones" && <DeliveryZonesPanel />}

      {tab === "restaurants" && (
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              onClick={() => { setPrimaryId(selectedRestos[0] || null); setLinkModal(true); }}
              disabled={selectedRestos.length < 2}
              data-testid="link-chain-btn"
              className="inline-flex items-center gap-1.5 text-xs rounded-full bg-ink text-white px-3.5 py-2 disabled:opacity-40"
            >
              <Store className="w-3.5 h-3.5" /> Lier comme succursales ({selectedRestos.length})
            </button>
            <button
              onClick={importRfc}
              data-testid="import-rfc-btn"
              className="inline-flex items-center gap-1.5 text-xs rounded-full border border-sand px-3.5 py-2 hover:bg-elevated"
              title="Importe l'enseigne RFC (5 succursales + plats + comptes) si absente — utile après un déploiement en production"
            >
              <Package className="w-3.5 h-3.5" /> Importer RFC (5 succursales)
            </button>
            <span className="text-xs text-mute">Cochez au moins 2 restaurants pour les lier en enseigne (affichage client unique).</span>
          </div>
          <div className="relative max-w-md mb-3">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-mute" />
            <input
              value={restoSearch}
              onChange={(e) => setRestoSearch(e.target.value)}
              placeholder={tr("admin.searchResto")}
              data-testid="admin-restos-search"
              className="w-full bg-white border border-sand rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:border-brand-red"
            />
          </div>
          <div className="bg-white border border-sand rounded-2xl p-2 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-elevated text-mute uppercase text-xs tracking-widest">
              <tr><th className="p-3"></th><th className="p-3 text-left">Nom</th><th>Zone</th><th>Enseigne</th><th>Note</th><th>Statut</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filteredRestaurants.map((r) => (
                <tr key={r.restaurant_id} className="border-t border-sand">
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedRestos.includes(r.restaurant_id)}
                      onChange={() => toggleSelectResto(r.restaurant_id)}
                      data-testid={`chain-select-${r.restaurant_id}`}
                      className="accent-brand-red w-4 h-4"
                    />
                  </td>
                  <td className="p-3 font-semibold">{r.name}</td>
                  <td className="text-center">{r.zone}</td>
                  <td className="text-center">
                    {r.chain ? (
                      <span className="inline-flex items-center gap-1">
                        <span data-testid={`chain-badge-${r.restaurant_id}`} className="text-[10px] uppercase tracking-widest bg-[#FFF3CD] text-[#946A00] px-2 py-0.5 rounded-full">
                          {r.chain_primary ? "enseigne ★" : "succursale"}
                        </span>
                        <button onClick={() => unlinkChain(r.restaurant_id)} data-testid={`chain-unlink-${r.restaurant_id}`} title="Délier de l'enseigne" className="text-[10px] underline text-mute hover:text-brand-red">délier</button>
                      </span>
                    ) : (
                      <span className="text-xs text-mute">—</span>
                    )}
                  </td>
                  <td className="text-center">⭐ {r.rating}</td>
                  <td className="text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${r.status === "active" ? "bg-[#E7F6EE] text-brand-green" : "bg-elevated text-mute"}`}>{r.status}</span>
                  </td>
                  <td className="text-right p-2 space-x-1">
                    <button
                      onClick={() => openDocs(r.restaurant_id)}
                      data-testid={`admin-resto-docs-${r.restaurant_id}`}
                      title="Documents fournis, visite terrain et note interne"
                      className="inline-flex items-center gap-1 text-xs rounded-full border border-sand px-2 py-1 hover:border-ink"
                    >
                      <FileText className="w-3 h-3" /> Documents
                    </button>
                    <button onClick={() => toggleOpen(r.restaurant_id, !r.open)} className="inline-flex items-center gap-1 text-xs rounded-full border border-sand px-2 py-1"><Power className="w-3 h-3" /> {r.open ? "Fermer" : "Ouvrir"}</button>
                    {r.status === "active" ? (
                      <button onClick={() => toggleStatus(r.restaurant_id, "suspended")} className="inline-flex items-center gap-1 text-xs rounded-full bg-elevated px-2 py-1 text-brand-red"><ShieldX className="w-3 h-3" /> Suspendre</button>
                    ) : (
                      <button onClick={() => toggleStatus(r.restaurant_id, "active")} className="inline-flex items-center gap-1 text-xs rounded-full bg-brand-green text-white px-2 py-1"><ShieldCheck className="w-3 h-3" /> Activer</button>
                    )}
                    <button
                      onClick={() => setDeleteTarget({ kind: "restaurant", id: r.restaurant_id, name: r.name, role: "restaurant" })}
                      data-testid={`admin-delete-resto-${r.restaurant_id}`}
                      title="Supprimer ce restaurant"
                      className="inline-flex items-center gap-1 text-xs rounded-full border border-brand-red/30 text-brand-red hover:bg-brand-red hover:text-white px-2 py-1"
                    >
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </button>
                  </td>
                </tr>
              ))}
              {filteredRestaurants.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-mute text-sm" data-testid="admin-restos-empty">Aucun restaurant ne correspond à « {restoSearch} »</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-3">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-mute" />
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder={tr("admin.searchUser")}
              data-testid="admin-users-search"
              className="w-full bg-white border border-sand rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:border-brand-red"
            />
          </div>
          <div className="bg-white border border-sand rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-elevated text-mute uppercase text-xs tracking-widest">
              <tr><th className="p-3 text-left">{tr("admin.thName")}</th><th>{tr("admin.thIdent")}</th><th>{tr("admin.thRole")}</th><th>{tr("admin.thPhone")}</th><th>{tr("admin.thActions")}</th></tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.user_id} className="border-t border-sand">
                  <td className="p-3">
                    {u.name}
                    {u.role === "driver" && <span className="ml-1.5 font-mono text-[11px] text-mute" data-testid={`driver-tag-${u.user_id}`}>{shortTag(u.user_id)}</span>}
                    {u.must_change_password && <TempPasswordBadge expiresAt={u.temp_password_expires_at} userId={u.user_id} />}
                    {u.role === "driver" && u.quiz_passed !== undefined && <QuizBadge user={u} />}
                  </td>
                  <td className="text-center">{u.phone || u.email || "—"}<div className="text-[10px] text-mute">{u.phone && u.email ? u.email : ""}</div></td>
                  <td className="text-center"><span className="text-xs uppercase tracking-widest text-brand-red">{u.role}</span></td>
                  <td className="text-center">{u.phone || "—"}</td>
                  <td className="text-center p-2">
                    {u.role !== "admin" && (
                      <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => resetPassword(u)}
                        data-testid={`admin-reset-pw-${u.user_id}`}
                        title="Réinitialiser le mot de passe (temporaire à communiquer via WhatsApp)"
                        className="inline-flex items-center gap-1 text-xs rounded-full border border-sand text-ink hover:border-ink px-2 py-1"
                      >
                        <KeyRound className="w-3 h-3" /> MDP
                      </button>
                      <button
                        onClick={() => setDeleteTarget({ kind: "user", id: u.user_id, name: u.name, role: u.role })}
                        data-testid={`admin-delete-user-${u.user_id}`}
                        title="Supprimer ce compte"
                        className="inline-flex items-center gap-1 text-xs rounded-full border border-brand-red/30 text-brand-red hover:bg-brand-red hover:text-white px-2 py-1"
                      >
                        <Trash2 className="w-3 h-3" /> Supprimer
                      </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-mute text-sm" data-testid="admin-users-empty">Aucun utilisateur ne correspond à « {userSearch} »</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-white border border-sand rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-elevated text-mute uppercase text-xs tracking-widest">
              <tr><th className="p-3 text-left">ID</th><th>Restaurant</th><th>Zone</th><th>Total</th><th>Statut</th><th>Contact</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} className="border-t border-sand">
                  <td className="p-3 font-mono text-xs">#{o.order_id.slice(-6).toUpperCase()}</td>
                  <td className="text-center">{o.restaurant_name}</td>
                  <td className="text-center">{o.zone}</td>
                  <td className="text-center">{formatGNF(o.total)}</td>
                  <td className="text-center"><span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[o.status]+"22", color: STATUS_COLOR[o.status] }}>{tr(`status.${o.status}`, STATUS_LABEL[o.status])}</span></td>
                  <td className="text-center">
                    {o.phone ? (
                      <a
                        href={`tel:${o.phone.replace(/[^+0-9]/g, "")}`}
                        data-testid={`admin-order-call-${o.order_id}`}
                        title="Appeler le client"
                        className="inline-flex items-center gap-1 text-xs rounded-full bg-elevated hover:bg-[#EBE8E0] text-ink px-2.5 py-1"
                      >
                        <Phone className="w-3 h-3" /> Appeler
                      </a>
                    ) : "—"}
                  </td>
                  <td className="text-center">
                    {o.status === "delivering" ? (
                      <button
                        onClick={() => setCancelInDelivery(o)}
                        data-testid={`admin-order-cid-${o.order_id}`}
                        title="Annulation en cours de livraison (décision admin)"
                        className="inline-flex items-center gap-1 text-xs rounded-full bg-brand-red text-white hover:bg-[#C11E2C] transition-colors px-2.5 py-1"
                      >
                        Annuler (en livraison)
                      </button>
                    ) : !["delivered", "cancelled", "cancelled_in_delivery"].includes(o.status) ? (
                      <button
                        onClick={() => setCancelTarget(o)}
                        data-testid={`admin-order-cancel-${o.order_id}`}
                        title="Annuler la commande (motif obligatoire)"
                        className="inline-flex items-center gap-1 text-xs rounded-full border border-brand-red/40 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-2.5 py-1"
                      >
                        Annuler
                      </button>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "reviews" && (
        <div className="space-y-3" data-testid="admin-reviews-list">
          {reviews.length === 0 && <div className="bg-white border border-sand rounded-2xl p-10 text-center text-mute">Aucun avis.</div>}
          {reviews.map((r) => (
            <div key={r.review_id} data-testid={`admin-review-${r.review_id}`} className={`bg-white border rounded-2xl p-4 ${r.status === "pending" ? "border-[#F4C430] shadow-[0_0_0_1px_#F4C430]" : r.hidden ? "border-mute opacity-70" : r.flagged ? "border-[#F4C430]" : "border-sand"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold">{r.user_name}</span>
                    <span className="text-xs text-mute">→ {r.restaurant_name}</span>
                    <div className="flex">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-[#F4C430] text-[#F4C430]" : "text-[#E5E1D8]"}`}/>)}</div>
                    {r.status === "pending" && <span data-testid={`review-badge-pending-${r.review_id}`} className="text-[10px] uppercase tracking-widest bg-[#FFF3CD] text-[#946A00] px-2 py-0.5 rounded-full font-semibold">À valider</span>}
                    {(r.hidden || r.status === "hidden") && <span className="text-[10px] uppercase tracking-widest bg-elevated text-mute px-2 py-0.5 rounded-full">{tr("admin.reviewsTab.hidden")}</span>}
                    {r.flagged && <span className="text-[10px] uppercase tracking-widest bg-[#FFF3CD] text-[#946A00] px-2 py-0.5 rounded-full">{tr("admin.reviewsTab.flagged")}</span>}
                  </div>
                  <div className="text-xs text-mute mb-2">{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
                  {r.comment && <p className="text-sm text-ink">{r.comment}</p>}
                  {r.photos?.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {r.photos.map((p, i) => (
                        <button key={i} type="button" onClick={() => setLightbox({ photos: r.photos, index: i })} data-testid={`admin-client-photo-${r.review_id}-${i}`}>
                          <img src={imgUrl(p)} alt={`photo client ${i+1}`} className="w-20 h-20 rounded-lg object-cover hover:opacity-90 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  )}
                  {r.reply && (
                    <div className={`mt-2 ml-2 pl-2 border-l-2 rounded-r px-3 py-2 text-xs ${r.reply_status === "pending" ? "border-[#F4C430] bg-[#FFF9E6]" : r.reply_status === "rejected" ? "border-[#E32636] bg-[#FDECEE]" : "border-brand-red bg-[#FFF5F6]"}`}>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-brand-red">{r.reply_author} :</span>
                        {r.reply_status === "pending" && <span data-testid={`reply-badge-pending-${r.review_id}`} className="text-[9px] uppercase tracking-widest bg-[#FFF3CD] text-[#946A00] px-2 py-0.5 rounded-full font-semibold">{tr("admin.replyToValidate")}</span>}
                        {r.reply_status === "approved" && <span className="text-[9px] uppercase tracking-widest bg-[#E7F6EE] text-brand-green px-2 py-0.5 rounded-full">{tr("admin.published")}</span>}
                        {r.reply_status === "rejected" && <span className="text-[9px] uppercase tracking-widest bg-[#FDECEE] text-[#E32636] px-2 py-0.5 rounded-full">{tr("admin.refused")}</span>}
                      </div>
                      <span className="text-ink">{r.reply}</span>
                      {r.reply_photos?.length > 0 && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          {r.reply_photos.map((p, i) => (
                            <button key={i} type="button" onClick={() => setLightbox({ photos: r.reply_photos, index: i })} data-testid={`admin-reply-photo-${r.review_id}-${i}`}>
                              <img src={imgUrl(p)} alt={`photo ${i+1}`} className="w-20 h-20 rounded-lg object-cover hover:opacity-90 transition-opacity" />
                            </button>
                          ))}
                        </div>
                      )}
                      {r.reply_status === "rejected" && r.reply_reject_reason && (
                        <div className="text-[#E32636] mt-1">Motif : {r.reply_reject_reason}</div>
                      )}
                      {r.reply_status === "pending" && (
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => moderate(r.review_id, "approve_reply")} data-testid={`mod-approve-reply-${r.review_id}`} className="text-xs inline-flex items-center gap-1 rounded-full bg-brand-green text-white px-3 py-1"><Eye className="w-3 h-3"/> {tr("admin.validateReply")}</button>
                          <button onClick={() => setRejectTarget(r)} data-testid={`mod-reject-reply-${r.review_id}`} className="text-xs inline-flex items-center gap-1 rounded-full bg-[#FDECEE] text-[#E32636] px-3 py-1"><EyeOff className="w-3 h-3"/> Refuser</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {r.status === "pending" && (
                    <button onClick={() => moderate(r.review_id, "approve")} data-testid={`mod-approve-${r.review_id}`} className="text-xs inline-flex items-center gap-1 rounded-full bg-brand-green text-white px-3 py-1"><Eye className="w-3 h-3"/> Valider</button>
                  )}
                  {(r.hidden || r.status === "hidden") ? (
                    <button onClick={() => moderate(r.review_id, "show")} data-testid={`mod-show-${r.review_id}`} className="text-xs inline-flex items-center gap-1 rounded-full bg-brand-green text-white px-3 py-1"><Eye className="w-3 h-3"/> Remettre</button>
                  ) : (
                    <button onClick={() => moderate(r.review_id, "hide")} data-testid={`mod-hide-${r.review_id}`} className="text-xs inline-flex items-center gap-1 rounded-full bg-elevated text-brand-red px-3 py-1"><EyeOff className="w-3 h-3"/> Masquer</button>
                  )}
                  {r.flagged ? (
                    <button onClick={() => moderate(r.review_id, "unflag")} data-testid={`mod-unflag-${r.review_id}`} className="text-xs inline-flex items-center gap-1 rounded-full bg-elevated text-ink px-3 py-1"><Flag className="w-3 h-3"/> Lever</button>
                  ) : (
                    <button onClick={() => moderate(r.review_id, "flag")} data-testid={`mod-flag-${r.review_id}`} className="text-xs inline-flex items-center gap-1 rounded-full border border-sand text-[#946A00] px-3 py-1"><Flag className="w-3 h-3"/> Signaler</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {deleteTarget && (
        <DeleteAccountModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); fetchAll(); }}
        />
      )}
      {rejectTarget && (
        <RejectReplyModal
          review={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onConfirm={rejectReply}
        />
      )}
      {lightbox && (
        <PhotoLightbox photos={lightbox.photos} initialIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}
      {cancelTarget && (
        <CancelOrderModal
          open={!!cancelTarget}
          orderId={cancelTarget.order_id}
          role="admin"
          showCompensation={cancelTarget.status !== "received"}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => { setCancelTarget(null); fetchAll(); }}
        />
      )}
      {docsTarget && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="resto-docs-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">Documents — {docsTarget.restaurant_name}</h3>
              <button onClick={() => setDocsTarget(null)} data-testid="resto-docs-close" className="text-mute hover:text-ink text-sm font-semibold">Fermer ✕</button>
            </div>
            <RestaurantDocuments
              application={docsTarget.application}
              payout={docsTarget.payout}
              verification={docsTarget.verification}
              saveUrl={`/admin/restaurants/${docsTarget.restaurant_id}/verification`}
              onSaved={() => openDocs(docsTarget.restaurant_id)}
            />
          </div>
        </div>
      )}
      {cancelInDelivery && (
        <CancelInDeliveryModal
          order={cancelInDelivery}
          onClose={() => setCancelInDelivery(null)}
          onDone={() => { setCancelInDelivery(null); fetchAll(); }}
        />
      )}
      {resetResult && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="reset-pw-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-2"><KeyRound className="w-5 h-5 text-brand-red" /> {tr("admin.tempPwdTitle")}</h3>
            <p className="text-sm text-mute mb-4">{tr("admin.tempPwdHint1")} <b>{resetResult.name}</b> {tr("admin.tempPwdHint2")} <b>{tr("admin.tempPwdHint3")}</b> {tr("admin.tempPwdHint4")}</p>
            {resetResult.email_sent && (
              <p className="text-sm mb-4 rounded-xl bg-[#E7F6EE] text-brand-green px-3 py-2" data-testid="reset-email-sent">
                ✉️ Mot de passe provisoire envoyé automatiquement par email à <b>{resetResult.email}</b>
              </p>
            )}
            {resetResult.email && !resetResult.email_sent && (
              <p className="text-sm mb-4 rounded-xl bg-[#FDECEE] text-brand-red px-3 py-2" data-testid="reset-email-failed">
                ⚠️ Échec de l'envoi email à {resetResult.email}{resetResult.email_error ? ` (${resetResult.email_error})` : ""} — utilisez WhatsApp.
              </p>
            )}
            <div className="rounded-xl bg-elevated px-4 py-3 text-center font-mono text-2xl font-bold tracking-wider mb-4 select-all" data-testid="reset-pw-value">
              {resetResult.temp_password}
            </div>
            {resetResult.phone && (
              <a
                href={`https://wa.me/${resetResult.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
                  `Bonjour ${resetResult.name}, votre mot de passe NelDaari a été réinitialisé. Mot de passe temporaire : ${resetResult.temp_password} — valable 24 heures, vous devrez le changer à la connexion.`
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid="reset-pw-whatsapp"
                className="w-full rounded-full bg-brand-green hover:bg-[#007A4A] text-white py-2.5 font-semibold inline-flex items-center justify-center gap-2 mb-2 text-sm"
              >
                <MessageCircle className="w-4 h-4" /> Envoyer via WhatsApp ({resetResult.phone})
              </a>
            )}
            <button onClick={() => setResetResult(null)} data-testid="reset-pw-close" className="w-full rounded-full border border-sand hover:border-ink py-2.5 text-sm font-semibold">
              Fermer
            </button>
          </div>
        </div>
      )}
      {linkModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="link-chain-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="font-display text-lg font-semibold mb-1">Lier comme succursales</h3>
            <p className="text-sm text-mute mb-4">Ces {selectedRestos.length} restaurants apparaîtront comme UNE seule enseigne côté client (une carte par quartier). Choisissez la succursale affichée par défaut :</p>
            <div className="space-y-2 mb-5 max-h-56 overflow-y-auto">
              {selectedRestos.map((rid) => {
                const r = restaurants.find((x) => x.restaurant_id === rid);
                return (
                  <label key={rid} className={`flex items-center gap-2.5 rounded-xl border p-3 cursor-pointer ${primaryId === rid ? "border-brand-red bg-[#FFF5F6]" : "border-sand"}`}>
                    <input type="radio" name="primary" checked={primaryId === rid} onChange={() => setPrimaryId(rid)} data-testid={`chain-primary-${rid}`} className="accent-brand-red" />
                    <span className="text-sm font-medium">{r?.name} <span className="text-xs text-mute">· {r?.zone}</span></span>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setLinkModal(false)} className="text-sm rounded-full px-4 py-2 border border-sand" data-testid="link-chain-cancel">Annuler</button>
              <button onClick={linkChain} disabled={!primaryId} data-testid="link-chain-confirm" className="text-sm rounded-full bg-ink text-white px-4 py-2 font-semibold disabled:opacity-50">Lier l'enseigne</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white border border-sand rounded-2xl p-4">
      <div className="w-10 h-10 rounded-full grid place-items-center mb-2" style={{ backgroundColor: color + "22", color }}>{icon}</div>
      <div className="text-xs uppercase tracking-widest text-mute">{label}</div>
      <div className="font-display text-xl sm:text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

/** Badge affiché à côté des comptes ayant un mot de passe temporaire en attente
 * de changement : temps restant avant expiration (24 h) ou "Expiré". */
/** Badge quiz d'intégration livreur (fiche admin) :
 * "Quiz réussi le [date], tentative n°X" ou "Quiz en attente — X tentative(s)". */
function QuizBadge({ user }) {
  const passed = user.quiz_passed === true;
  const attempts = user.quiz_attempts || 0;
  const label = passed
    ? `Quiz réussi le ${user.quiz_passed_at ? new Date(user.quiz_passed_at).toLocaleDateString("fr-FR") : "—"}${user.quiz_passed_attempt ? `, tentative n°${user.quiz_passed_attempt}` : ""}`
    : `Quiz en attente — ${attempts} tentative${attempts > 1 ? "s" : ""}`;
  return (
    <span
      data-testid={`quiz-badge-${user.user_id}`}
      className={`ml-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${passed ? "bg-[#E7F6EE] text-brand-green" : "bg-brand-yellow/30 text-[#8A6D00]"}`}
    >
      {label}
    </span>
  );
}

function TempPasswordBadge({ expiresAt, userId }) {
  let label = "MDP temporaire en attente";
  let expired = false;
  if (expiresAt) {
    const remaining = new Date(expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      expired = true;
      label = "MDP temporaire · Expiré";
    } else {
      const h = Math.floor(remaining / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      label = `MDP temporaire · expire dans ${h > 0 ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`}`;
    }
  }
  return (
    <span
      data-testid={`temp-pw-badge-${userId}`}
      className={`ml-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${expired ? "bg-brand-red text-white" : "bg-brand-yellow/30 text-[#8A6D00]"}`}
    >
      {label}
    </span>
  );
}
